import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@neo/primitives/collapsible'
import { ChevronDown, ChevronRight } from 'lucide-react'
import { useState } from 'react'
import { SuspenseLoader } from 'v2/shared/components/suspense-loader'
import type { CustomField } from 'v2/shared/services/external-apps-service'
import { CustomFieldsTable } from './custom-fields-table'

interface EntityFieldsSectionProps {
  entity: string
  fields: CustomField[]
  isLoading: boolean
  onFieldsChange: (entity: string, fields: CustomField[]) => void
  defaultOpen?: boolean
}

export const EntityFieldsSection = ({
  entity,
  fields,
  isLoading,
  onFieldsChange,
  defaultOpen = false,
}: EntityFieldsSectionProps) => {
  const [isOpen, setIsOpen] = useState(defaultOpen)

  const handleFieldsChange = (updatedFields: CustomField[]) => {
    onFieldsChange(entity, updatedFields)
  }

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen} className="border-b border-st1">
      <CollapsibleTrigger
        className="flex w-full items-center gap-2 py-3 px-1 hover:bg-hv1 transition-colors"
        data-testid={`collapsible-trigger-${entity}`}
      >
        {isOpen ? (
          <ChevronDown className="h-4 w-4 text-secondary" />
        ) : (
          <ChevronRight className="h-4 w-4 text-secondary" />
        )}
        <span className="text-p-600 capitalize">{entity}</span>
        {!isLoading && <span className="text-s-400 text-secondary">({fields.length} fields)</span>}
      </CollapsibleTrigger>
      <CollapsibleContent>
        {isLoading ? (
          <div className="relative h-[150px]">
            <SuspenseLoader text={`Fetching ${entity} fields...`} />
          </div>
        ) : (
          <div className="pb-3">
            <CustomFieldsTable
              fields={fields}
              onFieldsChange={handleFieldsChange}
              entity={entity}
            />
          </div>
        )}
      </CollapsibleContent>
    </Collapsible>
  )
}
